import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc, serverTimestamp, deleteUser } from 'firebase/firestore';
import { reauthenticateWithCredential, updateEmail, updatePassword, EmailAuthProvider, signOut } from 'firebase/auth';

import { useToast } from '../contexts/ToastContext';
import PhoneInput from '../components/ui/PhoneInput';
import PlannerIcons from '../components/shared/PlannerIcons'; 
import ProfileEditModal from '../features/auth/ProfileEditModal'; 

export default function UpdatedSettings({ user, becomeVendor, isUpgrading, navigateTo, functions, db }) {
    const [vacationSettings, setVacationSettings] = useState(user.vacationSettings || { enabled: false, startDate: '', endDate: '' });
    const [bookingConfirmation, setBookingConfirmation] = useState(user.bookingConfirmation || 'automatic');
    const [saveStatus, setSaveStatus] = useState('');
    const [showManageMembership, setShowManageMembership] = useState(false);
    const [showProfileEdit, setShowProfileEdit] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    
    const toast = useToast();

    const [profileData, setProfileData] = useState({
        plannerName: user.plannerName || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        dob: user.dob || '',
        phone: user.phone || '',
        address: user.address || { line1: '', city: '', state: '', postal_code: '' }
    });

    const handleProfileChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, [name]: value }));
    };

    const handleAddressChange = (e) => {
        const { name, value } = e.target;
        setProfileData(prev => ({ ...prev, address: { ...prev.address, [name]: value } }));
    };

    const handleSave = async () => {
        const userRef = doc(db, "users", user.uid);
        await updateDoc(userRef, {
            vacationSettings,
            bookingConfirmation,
            ...profileData
        });
        setSaveStatus("Settings saved successfully!");
        setTimeout(() => setSaveStatus(''), 3000);
    };

    return (
        <div className="container mx-auto max-w-2xl py-8">
            <div className="flex items-center gap-3 mb-6 flex-wrap">
                <h2 className="text-3xl font-bold">Account Settings</h2>
            </div>
            
            <div className="bg-white p-6 rounded-lg shadow-lg space-y-6">
                <div>
                    <h3 className="text-xl font-bold">Account Type</h3>
                    <p className="text-gray-600 mt-1">Your current account type is: <span className="font-semibold">{user.isVendor ? 'Vendor' : 'Planner'}</span></p>
                    
                    {user.isPro && (
                        <div className="mt-4 p-4 bg-gradient-to-r from-pink-50 to-purple-50 border border-pink-100 rounded-xl flex justify-between items-center">
                            <div>
                                <p className="font-bold text-gray-900 text-lg">✨ {user.isPro === 'personal' ? 'Personal Pro Plan' : 'Host-it! Pro Plan'}</p>
                                <p className="text-sm text-gray-500 mt-0.5">Active Subscription</p>
                            </div>
                            <button onClick={() => setShowManageMembership(true)} className="bg-white border border-gray-200 px-4 py-2 rounded-lg font-bold shadow-sm">
                                Manage Plan
                            </button>
                        </div>
                    )}

                    {!user.isVendor && (
                        <div className="mt-4">
                            <p className="text-gray-600">Upgrade to a vendor account to list your own party supplies for rent.</p>
                            <button onClick={becomeVendor} disabled={isUpgrading} className="mt-2 bg-green-500 text-white px-4 py-2 rounded-full font-bold">
                                {isUpgrading ? 'Upgrading...' : 'Become a Vendor'}
                            </button>
                        </div>
                    )}
                </div>

                <div className="border-t pt-6">
                    <h3 className="text-xl font-bold">{user.isVendor ? "Personal & Verification Details" : "Profile Information"}</h3>
                    <div className="space-y-4 mt-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                             <input type="text" name="plannerName" placeholder="Display/Planner Name" value={profileData.plannerName} onChange={handleProfileChange} className="p-2 border rounded-md" />
                            <input type="text" name="firstName" placeholder="Legal First Name" value={profileData.firstName} onChange={handleProfileChange} className="p-2 border rounded-md" />
                            <input type="text" name="lastName" placeholder="Legal Last Name" value={profileData.lastName} onChange={handleProfileChange} className="p-2 border rounded-md" />
                        </div>
                         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="text-xs text-gray-500">Date of Birth</label>
                                <input type="date" name="dob" value={profileData.dob} onChange={handleProfileChange} className="w-full p-2 border rounded-md" />
                            </div>
                            <input type="tel" name="phone" placeholder="Phone Number" value={profileData.phone} onChange={handleProfileChange} className="p-2 border rounded-md self-end" />
                        </div>
                         <div>
                            <label className="text-xs text-gray-500">Address</label>
                            <input type="text" name="line1" placeholder="Street Address" value={profileData.address.line1} onChange={handleAddressChange} className="w-full p-2 border rounded-md mb-2" />
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                                <input type="text" name="city" placeholder="City" value={profileData.address.city} onChange={handleAddressChange} className="p-2 border rounded-md" />
                                <input type="text" name="state" placeholder="State" value={profileData.address.state} onChange={handleAddressChange} className="p-2 border rounded-md" />
                                <input type="text" name="postal_code" placeholder="ZIP" value={profileData.address.postal_code} onChange={handleAddressChange} className="p-2 border rounded-md" />
                            </div>
                        </div>
                    </div>
                </div>
                
                {user.isVendor && (
                    <>
                        <div className="border-t pt-6">
                            <h3 className="text-xl font-bold mb-4 text-gray-900">Business Profile & Storefront</h3>
                            <div className="bg-gray-50 p-5 rounded-xl border border-gray-200 flex justify-between items-center">
                                <div>
                                    <h4 className="font-bold text-gray-800">Public Business Info</h4>
                                    <p className="text-sm text-gray-600">Update your store name, logo, cover photo, and description.</p>
                                </div>
                                <button onClick={() => setShowProfileEdit(true)} className="bg-white border border-gray-300 px-5 py-2.5 rounded-lg font-bold shadow-sm">
                                    Edit Business Profile
                                </button>
                            </div>
                        </div>

                        <div className="border-t pt-6">
                            <h3 className="text-xl font-bold">Storefront Settings</h3>
                            <div className="mt-4 space-y-4">
                                <div className="bg-gray-50 p-3 rounded-lg">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <label className="font-semibold text-gray-800">Vacation Mode</label>
                                            <p className="text-sm text-gray-500">Schedule time away. Listings cannot be booked during this period.</p>
                                        </div>
                                        <input type="checkbox" checked={vacationSettings.enabled} onChange={(e) => setVacationSettings({...vacationSettings, enabled: e.target.checked})} className="w-5 h-5 text-pink-600 rounded" />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </>
                )}
                
                <button onClick={handleSave} className="w-full mt-6 bg-blue-500 text-white py-2 rounded-lg font-semibold hover:bg-blue-600 transition">Save All Settings</button>
                {saveStatus && <p className="text-green-600 text-center mt-4 font-semibold">{saveStatus}</p>}

                <div className="border-t pt-8 mt-8 border-red-100">
                    <h3 className="text-xl font-bold mb-4 text-red-600">Danger Zone</h3>
                    <button onClick={() => setShowDeleteModal(true)} className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold hover:bg-red-700 transition">
                        Delete Account
                    </button>
                </div>
            </div>
        </div>
    );
}
